// Authentication API
import type { AppLanguage } from '@/services/i18n/languages'
import { apiClient } from './client'

/** The `preferred_language` wire value — the set the UI ships, so adding a
 *  language to `APP_LANGUAGES` widens this too (the API's Literal must match). */
export type PreferredLanguage = AppLanguage

/** Global authorization tier (mirrors the backend GlobalRole). Ordered
 * user < admin < superadmin. `is_superuser` stays for back-compat (== superadmin). */
export type GlobalRole = 'user' | 'admin' | 'superadmin'

export interface User {
  id: string
  username: string
  full_name: string | null
  email: string | null
  is_active: boolean
  is_superuser: boolean
  role: GlobalRole
  created_at: string
  preferred_language: PreferredLanguage
  /**
   * The operator's unit, which prefills the capturing unit of a HUMINT encounter (ADR-0051).
   * Only while the HUMINT extension is on: absent or null otherwise.
   */
  unit?: string | null
}

export interface UpdateMeRequest {
  preferred_language?: PreferredLanguage
  /** Only while HUMINT is on (the API refuses it otherwise); null or blank clears it. */
  unit?: string | null
}

export interface LoginRequest {
  username: string
  password: string
}

export interface RegisterRequest {
  username: string
  password: string
  full_name?: string
  email?: string
}

export interface TokenResponse {
  access_token: string
  token_type: string
}

export const authApi = {
  /**
   * Login with username and password.
   * Returns access token and sets refresh token in httpOnly cookie.
   */
  login: async (data: LoginRequest): Promise<TokenResponse> => {
    // Use URLSearchParams for form data (OAuth2PasswordRequestForm)
    const formData = new URLSearchParams()
    formData.append('username', data.username)
    formData.append('password', data.password)

    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: formData.toString(),
      credentials: 'include', // Important: send/receive cookies
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Login failed' }))
      throw new Error(error.detail || 'Invalid credentials')
    }

    return response.json()
  },

  /**
   * Register a new user.
   */
  register: async (data: RegisterRequest): Promise<User> => {
    return apiClient.post<User>('/auth/register', data)
  },

  /**
   * Get current authenticated user.
   */
  me: async (): Promise<User> => {
    return apiClient.get<User>('/auth/me')
  },

  /**
   * Update self-managed fields on the current user (e.g. preferred_language).
   */
  updateMe: async (data: UpdateMeRequest): Promise<User> => {
    return apiClient.patch<User>('/auth/me', data)
  },

  /**
   * Refresh access token using refresh token cookie.
   */
  refresh: async (): Promise<TokenResponse> => {
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      credentials: 'include', // Send refresh token cookie
    })

    if (!response.ok) {
      throw new Error('Token refresh failed')
    }

    return response.json()
  },

  /**
   * Logout - clears refresh token cookie.
   */
  logout: async (): Promise<void> => {
    await fetch('/api/auth/logout', {
      method: 'POST',
      credentials: 'include',
    })
  },
}
